import React, { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, TextField, InputAdornment, IconButton, Box } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";

const buildLabelFromPerson = (p) => {
  const id = String(p?.identificacion || "").trim();
  const name = String(p?.razon_social || "").trim();
  if (id && name) return `${id} - ${name}`;
  return name || id || "";
};

const normalizeToOption = (val) => {
  if (!val) return null;

  if (typeof val === "string") {
    const name = val.trim();
    if (!name) return null;
    return { id: name, label: name, data: null };
  }

  if (typeof val === "object") {
    if ("label" in val && "data" in val) {
      const label = String(val.label || "").trim();
      const data = val.data || null;
      const id = String(val.id || data?.id || data?.identificacion || label || "").trim();
      if (!label && data) {
        const l2 = buildLabelFromPerson(data);
        return { id: id || l2, label: l2, data };
      }
      return { id: id || label, label, data };
    }

    const data = val;
    const label = buildLabelFromPerson(data);
    const id = String(data?.id || data?.identificacion || label || "").trim();
    if (!label) return null;
    return { id: id || label, label, data };
  }

  return null;
};

export default function PersonaAutocomplete({ value, onChange, tipoDocumento }) {
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selected, setSelected] = useState(null);

  const lastTipoRef = useRef(tipoDocumento);
  const lastEmittedRef = useRef("");

  const incoming = useMemo(() => normalizeToOption(value), [value]);

  useEffect(() => {
    if (lastTipoRef.current !== tipoDocumento) {
      lastTipoRef.current = tipoDocumento;
      setPersonas([]);
      setLoading(false);
      setSelected(null);
      setInputValue("");
      lastEmittedRef.current = "";
      if (onChange) onChange(null);
    }
  }, [tipoDocumento, onChange]);

  useEffect(() => {
    const next = incoming;
    if (!next) {
      setSelected(null);
      setInputValue("");
      lastEmittedRef.current = "";
      return;
    }

    setSelected(next);
    setInputValue(String(next.label || ""));

    const hasRealData = !!(next.data && (next.data.identificacion || next.data.id));
    if (hasRealData) {
      const key = String(next.data.identificacion || next.data.id || next.id || "");
      if (key && lastEmittedRef.current !== key) {
        lastEmittedRef.current = key;
        if (onChange) onChange(next.data);
      }
    }
  }, [incoming, onChange]);

  const buscarPersonas = async (texto) => {
    const q = String(texto || "").trim();
    if (!q || q.length < 2 || !tipoDocumento) {
      setPersonas([]);
      return;
    }

    setLoading(true);
    try {
      const tipo = String(tipoDocumento).toLowerCase();
      const res = await fetch(`/facturas/personas/buscar/?q=${encodeURIComponent(q)}&tipo=${tipo}`, {
        credentials: "include",
      });
      const ct = (res.headers.get("content-type") || "").toLowerCase();
      if (!ct.includes("application/json")) {
        setPersonas([]);
        return;
      }
      const json = await res.json();

      if (res.ok && Array.isArray(json?.personas)) {
        setPersonas(
          json.personas
            .map((p) => {
              const label = buildLabelFromPerson(p);
              const id = String(p?.id || p?.identificacion || label || "");
              return label ? { id, label, data: p } : null;
            })
            .filter(Boolean)
        );
      } else {
        setPersonas([]);
      }
    } catch {
      setPersonas([]);
    } finally {
      setLoading(false);
    }
  };

  const esProveedor = String(tipoDocumento || "").toUpperCase() === "PROVEEDOR";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Autocomplete
        options={personas}
        loading={loading}
        value={selected}
        inputValue={inputValue}
        onInputChange={(e, val, reason) => {
          if (reason === "reset") return;

          if (reason === "clear") {
            setInputValue("");
            setPersonas([]);
            setSelected(null);
            lastEmittedRef.current = "";
            if (onChange) onChange(null);
            return;
          }

          const nextText = String(val || "");
          setInputValue(nextText);

          if (!nextText) {
            setPersonas([]);
            setSelected(null);
            lastEmittedRef.current = "";
            if (onChange) onChange(null);
            return;
          }

          buscarPersonas(nextText);
        }}
        onChange={(e, option) => {
          if (!option) {
            setSelected(null);
            setInputValue("");
            setPersonas([]);
            lastEmittedRef.current = "";
            if (onChange) onChange(null);
            return;
          }

          setSelected(option);
          setInputValue(String(option?.label || ""));
          setPersonas([]);

          const key = String(option?.data?.identificacion || option?.data?.id || option?.id || "");
          lastEmittedRef.current = key || lastEmittedRef.current;

          if (onChange) onChange(option?.data || null);
        }}
        isOptionEqualToValue={(opt, val) => String(opt?.id || "") === String(val?.id || "")}
        getOptionLabel={(option) => String(option?.label || "")}
        noOptionsText={`No se encontraron ${esProveedor ? "proveedores" : "clientes"}`}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={`Buscar ${esProveedor ? "proveedor" : "cliente"}...`}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#888" }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 600 }}
          />
        )}
      />

      <IconButton
        size="small"
        sx={{
          bgcolor: "#1976d2",
          color: "#fff",
          borderRadius: 1,
          "&:hover": { bgcolor: "#1565c0" },
        }}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
